// Third-parties
import React from 'react';
import moment from 'moment';

// Types
import { Announcement } from '../../../redux/modules/types/announcements';

// Components
import DataTable from '../data/DataTable';
import { IconButton } from '../buttons';

// Utils
import { formatLocalizedString, formatMessage, FormattedMessage } from '../../../utils/i18n';
import LocalizedString from '@/types/localized-string';

type Props = {
  announcements: Announcement[];
  hasMore?: boolean;
  noDataText?: string;
  onDelete: (announcementId: string) => void;
  onRowClick: (announcementId: string) => void;
};

class AnnouncementsTable extends React.Component<Props> {
  getAcknowledgePercentage = (total: number, done: number) =>
    done === total ? (
      formatMessage('Common.All')
    ) : (
      <>
        {Math.round((done / total) * 100)}
        <span className="percent-sign">%</span>
      </>
    );

  getTableColumns = () => {
    const { onDelete } = this.props;

    return [
      {
        key: 'title',
        accessor: 'title',
        minWidth: 320,
        className: 'title',
        Header: () => <span>{formatMessage('AnnouncementsAdmin.AnnouncementsTable.Headers.Title')}</span>,
        Cell: ({ value }: { value: LocalizedString }) => formatLocalizedString(value),
      },
      {
        key: 'author',
        className: 'author',
        Header: () => <span>{formatMessage('AnnouncementsAdmin.AnnouncementsTable.Headers.Author')}</span>,
        Cell: ({ original }: { original: Announcement }) => {
          const { createdByFirstName, createdByLastName } = original;
          if (!createdByFirstName && !createdByLastName) return <i className="icon-minus-med" />;
          return `${createdByFirstName || ''} ${createdByLastName || ''}`;
        },
      },
      {
        key: 'date',
        accessor: 'notificationDate',
        className: 'date',
        Header: () => <span>{formatMessage('AnnouncementsAdmin.AnnouncementsTable.Headers.DateSent')}</span>,
        Cell: ({ value }: { value: string }) => (value ? moment(value).format(formatMessage('Common.DateFormats.DateWithTime')) : null),
      },
      {
        id: 'colAcknowledged',
        key: 'acknowledgmentRate',
        width: 80,
        className: 'percentage',
        Header: () => <span>{formatMessage('AnnouncementsAdmin.AnnouncementsTable.Headers.Acknowledged')}</span>,
        Cell: ({ value }: { value: number }) => value || null,
        accessor: (item: any) => {
          const { acknowledgmentRequired, recipientCount, acknowledgmentCount } = item;
          return acknowledgmentRequired ? (
            this.getAcknowledgePercentage(recipientCount, acknowledgmentCount)
          ) : (
            <span className="not-applicable">
              <FormattedMessage id="Common.NotApplicable" />
            </span>
          );
        },
      },
      {
        key: 'deleteAction',
        accessor: '_id',
        width: 42,
        className: 'actions',
        Cell: ({ value }: { value: string }) => (
          <IconButton
            aria-label={formatMessage('AnnouncementsAdmin.AnnouncementsTable.Headers.DeleteAction')}
            icon="ds-delete"
            onClick={() => onDelete(value)}
          />
        ),
      },
    ];
  };

  render() {
    const { announcements, noDataText, hasMore, onRowClick } = this.props;

    return (
      <div className="announcements-table d-flex flex-column">
        <DataTable columns={this.getTableColumns()} data={announcements} noDataText={noDataText} showLoadMore={hasMore} onRowClick={onRowClick} />
      </div>
    );
  }
}

export default AnnouncementsTable;
